import { useEffect, useRef, useCallback, useState } from 'react';
import { getAuth } from 'firebase/auth';

const SSE_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

/**
 * Connection status enum
 */
export const SSEConnectionStatus = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error'
};

/**
 * Base SSE hook with connection management
 * @param {string} endpoint - SSE endpoint path (e.g., '/sse/events/:eventId')
 * @param {Object} options - Configuration options
 * @returns {Object} SSE connection state and controls
 */
export const useSSE = (endpoint, options = {}) => {
  const {
    autoConnect = true,
    onMessage,
    onError,
    onConnected,
    eventHandlers = {}
  } = options;

  const eventSourceRef = useRef(null);
  const [status, setStatus] = useState(SSEConnectionStatus.DISCONNECTED);
  const [lastMessage, setLastMessage] = useState(null);
  const [error, setError] = useState(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  // Cleanup function
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (eventSourceRef.current) {
      console.log('🔌 Closing SSE connection');
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setStatus(SSEConnectionStatus.DISCONNECTED);
    }
    
    reconnectAttempts.current = 0;
  }, []);

  // Connect function
  const connect = useCallback(async () => {
    if (!endpoint) {
      console.warn('SSE: No endpoint provided');
      return;
    }

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      setStatus(SSEConnectionStatus.CONNECTING);
      setError(null);

      // Get user ID from Firebase Auth
      const auth = getAuth();
      const user = auth.currentUser;
      const userId = user?.uid || 'anonymous';

      // Build URL with userId query param
      const url = new URL(`${SSE_BASE_URL}${endpoint}`);
      url.searchParams.set('userId', userId);

      console.log('🔌 Connecting to SSE:', url.toString());

      const eventSource = new EventSource(url.toString());
      eventSourceRef.current = eventSource;

      // Handle connection open
      eventSource.onopen = () => {
        console.log('✅ SSE connection established');
        setStatus(SSEConnectionStatus.CONNECTED);
        reconnectAttempts.current = 0;
        if (onConnected) onConnected();
      };

      // Handle generic messages
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📨 SSE message received:', data);
          setLastMessage(data);
          if (onMessage) onMessage(data);
        } catch (e) {
          console.error('Failed to parse SSE message:', e);
        }
      };

      // Handle errors
      eventSource.onerror = (err) => {
        console.error('❌ SSE error:', err);
        setError(err);
        setStatus(SSEConnectionStatus.ERROR);
        
        if (onError) onError(err);

        // Auto-reconnect logic
        if (eventSource.readyState === EventSource.CLOSED) {
          if (reconnectAttempts.current < maxReconnectAttempts) {
            reconnectAttempts.current += 1;
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
            console.log(`🔄 Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current}/${maxReconnectAttempts})`);
            
            reconnectTimeoutRef.current = setTimeout(() => {
              connect();
            }, delay);
          } else {
            console.error('Max reconnection attempts reached');
            setStatus(SSEConnectionStatus.DISCONNECTED);
          }
        }
      };

      // Register custom event handlers
      // SSE 'connected' event (sent by server on connection)
      eventSource.addEventListener('connected', (e) => {
        try {
          const data = JSON.parse(e.data);
          console.log('🤝 SSE connected event:', data);
          if (eventHandlers.connected) eventHandlers.connected(data);
        } catch (err) {
          console.error('Failed to parse connected event:', err);
        }
      });

      // RSVP update event
      eventSource.addEventListener('rsvp-update', (e) => {
        try {
          const data = JSON.parse(e.data);
          console.log('📝 RSVP update:', data);
          if (eventHandlers['rsvp-update']) eventHandlers['rsvp-update'](data);
        } catch (err) {
          console.error('Failed to parse rsvp-update event:', err);
        }
      });

      // RSVP summary event
      eventSource.addEventListener('rsvp-summary', (e) => {
        try {
          const data = JSON.parse(e.data);
          console.log('📊 RSVP summary:', data);
          if (eventHandlers['rsvp-summary']) eventHandlers['rsvp-summary'](data);
        } catch (err) {
          console.error('Failed to parse rsvp-summary event:', err);
        }
      });

      // Capacity update event
      eventSource.addEventListener('capacity-update', (e) => {
        try {
          const data = JSON.parse(e.data);
          console.log('📏 Capacity update:', data);
          if (eventHandlers['capacity-update']) eventHandlers['capacity-update'](data);
        } catch (err) {
          console.error('Failed to parse capacity-update event:', err);
        }
      });

      // Notification event
      eventSource.addEventListener('notification', (e) => {
        try {
          const data = JSON.parse(e.data);
          console.log('🔔 Notification:', data);
          if (eventHandlers.notification) eventHandlers.notification(data);
        } catch (err) {
          console.error('Failed to parse notification event:', err);
        }
      });

      // Live event update
      eventSource.addEventListener('live-event-update', (e) => {
        try {
          const data = JSON.parse(e.data);
          console.log('⚽ Live event update:', data);
          if (eventHandlers['live-event-update']) eventHandlers['live-event-update'](data);
        } catch (err) {
          console.error('Failed to parse live-event-update event:', err);
        }
      });

      // Event updated (general event data changes)
      eventSource.addEventListener('event-updated', (e) => {
        try {
          const data = JSON.parse(e.data);
          console.log('📅 Event updated:', data);
          if (eventHandlers['event-updated']) eventHandlers['event-updated'](data);
        } catch (err) {
          console.error('Failed to parse event-updated event:', err);
        }
      });

    } catch (err) {
      console.error('Failed to create SSE connection:', err);
      setError(err);
      setStatus(SSEConnectionStatus.ERROR);
    }
  }, [endpoint, onMessage, onError, onConnected, eventHandlers]);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect && endpoint) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, endpoint, connect, disconnect]);

  // Handle auth state changes
  useEffect(() => {
    const auth = getAuth();
    
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && status === SSEConnectionStatus.DISCONNECTED && autoConnect) {
        // User logged in, reconnect
        connect();
      } else if (!user && status === SSEConnectionStatus.CONNECTED) {
        // User logged out, disconnect
        disconnect();
      }
    });

    return unsubscribe;
  }, [connect, disconnect, status, autoConnect]);

  return {
    status,
    isConnected: status === SSEConnectionStatus.CONNECTED,
    lastMessage,
    error,
    connect,
    disconnect,
    reconnect: connect
  };
};

/**
 * Hook for event-specific SSE updates (RSVP, capacity, etc.)
 * @param {string} eventId - The event ID to subscribe to
 * @param {Object} options - Event handlers and options
 * @returns {Object} SSE connection state
 */
export const useEventSSE = (eventId, options = {}) => {
  const {
    onRSVPUpdate,
    onRSVPSummary,
    onCapacityUpdate,
    onEventUpdated,
    autoConnect = true
  } = options;

  const endpoint = eventId ? `/sse/events/${eventId}` : null;

  const eventHandlers = {
    'rsvp-update': (data) => {
      if (onRSVPUpdate && data.eventId === eventId) {
        onRSVPUpdate(data);
      }
    },
    'rsvp-summary': (data) => {
      if (onRSVPSummary && data.eventId === eventId) {
        onRSVPSummary(data);
      }
    },
    'capacity-update': (data) => {
      if (onCapacityUpdate && data.eventId === eventId) {
        onCapacityUpdate(data);
      }
    },
    'event-updated': (data) => {
      if (onEventUpdated && data.eventId === eventId) {
        onEventUpdated(data);
      }
    }
  };

  return useSSE(endpoint, {
    autoConnect: autoConnect && !!eventId,
    eventHandlers
  });
};

/**
 * Hook for user-specific notifications via SSE
 * @param {Object} options - Notification handlers and options
 * @returns {Object} SSE connection state
 */
export const useNotificationsSSE = (options = {}) => {
  const {
    onNotification,
    autoConnect = true
  } = options;

  const [userId, setUserId] = useState(null);

  // Get user ID from auth
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUserId(user?.uid || null);
    });
    return unsubscribe;
  }, []);

  const endpoint = userId ? `/sse/user/${userId}` : null;

  const eventHandlers = {
    notification: (data) => {
      if (onNotification) {
        onNotification(data);
      }
    }
  };

  return useSSE(endpoint, {
    autoConnect: autoConnect && !!userId,
    eventHandlers
  });
};

/**
 * Hook for live event/scorecard updates via SSE
 * @param {string} eventId - The event ID to subscribe to
 * @param {Object} options - Live event handlers and options
 * @returns {Object} SSE connection state
 */
export const useLiveEventSSE = (eventId, options = {}) => {
  const {
    onLiveUpdate,
    onScoreUpdate,
    autoConnect = true
  } = options;

  const endpoint = eventId ? `/sse/events/${eventId}` : null;

  const eventHandlers = {
    'live-event-update': (data) => {
      if (data.eventId === eventId) {
        if (onLiveUpdate) onLiveUpdate(data);
        if (onScoreUpdate && data.data?.scorecard) {
          onScoreUpdate(data.data.scorecard);
        }
      }
    },
    'event-updated': (data) => {
      if (data.eventId === eventId && onLiveUpdate) {
        onLiveUpdate(data);
      }
    }
  };

  return useSSE(endpoint, {
    autoConnect: autoConnect && !!eventId,
    eventHandlers
  });
};

/**
 * Hook that mimics the old useEventRSVPWebSocket interface for easier migration
 * @param {string} eventId - The event ID
 * @param {Object} rsvpHook - The RSVP hook instance for refresh
 * @returns {Object} Connection state (compatible with old WebSocket hook)
 */
export const useEventRSVPSSE = (eventId, rsvpHook) => {
  const handleRSVPUpdate = useCallback((data) => {
    if (data.eventId === eventId && rsvpHook?.refresh) {
      // Refresh RSVP data when updates are received
      rsvpHook.refresh();
    }
  }, [eventId, rsvpHook]);

  const handleNotification = useCallback((notification) => {
    // Handle RSVP-related notifications
    if (notification.type?.includes('rsvp') && notification.data?.eventId === eventId) {
      console.log('Received RSVP notification for current event:', notification);
    }
  }, [eventId]);

  const handleEventUpdate = useCallback((eventUpdate) => {
    if (eventUpdate.eventId === eventId) {
      console.log('Event updated:', eventUpdate);
      if (rsvpHook?.refresh) {
        rsvpHook.refresh();
      }
    }
  }, [eventId, rsvpHook]);

  const sse = useEventSSE(eventId, {
    onRSVPUpdate: handleRSVPUpdate,
    onEventUpdated: handleEventUpdate,
    autoConnect: !!eventId
  });

  // Also subscribe to user notifications if needed
  const notifications = useNotificationsSSE({
    onNotification: handleNotification,
    autoConnect: !!eventId
  });

  return {
    isConnected: sse.isConnected,
    status: sse.status,
    connect: sse.connect,
    disconnect: sse.disconnect,
    reconnect: sse.reconnect,
    // Compatibility methods (no-ops since SSE doesn't need room management)
    joinEventRoom: () => {}, // Room is joined via URL
    leaveEventRoom: () => {}, // Disconnect handles this
    socket: null // No socket in SSE
  };
};

export default useSSE;

